import React from "react"
import { twMerge } from "tailwind-merge"

import { getElementTextColor } from "../utils/colorUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

const GenericSection = ({ section, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const layout = settings.layout || "text-image"
  const fullWidth = settings.fullWidth || false
  const hasBackground = settings.hasBackground || false
  const showButton = settings.showButton || false

  // Get dynamic text colors based on background
  const sectionBackgroundColor = hasBackground
    ? theme?.colors?.bgLight || "#F5F5F5"
    : theme?.colors?.background || "#FFFFFF"
  const headingColor = getElementTextColor("heading", sectionBackgroundColor, theme)
  const bodyColor = getElementTextColor("body", sectionBackgroundColor, theme)

  const renderContent = () => {
    const contentElement = (
      <div>
        {section.body && (
          <div
            className="prose prose-lg max-w-none"
            style={{
              fontFamily: theme?.fonts?.paragraph || "sans-serif",
              color: bodyColor
            }}
            dangerouslySetInnerHTML={{ __html: section.body }}
          />
        )}
        {showButton && settings.buttonText && (
          <div className="mt-6">
            <a
              href={settings.buttonLink || "#"}
              className={`inline-block rounded px-6 py-3 text-sm font-medium transition-colors ${
                settings.buttonStyle === "primary"
                  ? "bg-teal-600 text-white hover:bg-teal-700"
                  : settings.buttonStyle === "secondary"
                  ? "border border-teal-600 text-teal-600 hover:bg-teal-50"
                  : "text-teal-600 underline hover:text-teal-700"
              }`}>
              {settings.buttonText}
            </a>
          </div>
        )}
      </div>
    )

    const imageElement = section.imageUrl && (
      <div>
        <img
          src={section.imageUrl}
          alt={section.headline || section.layout}
          className="h-auto w-full rounded-lg shadow-lg"
        />
      </div>
    )

    switch (layout) {
      case "text-only":
        return contentElement
      case "image-text":
        return (
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
            {imageElement}
            {contentElement}
          </div>
        )
      case "centered":
        return (
          <div className="mx-auto max-w-3xl text-center">
            {contentElement}
            {imageElement}
          </div>
        )
      case "text-image":
      default:
        return (
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
            {contentElement}
            {imageElement}
          </div>
        )
    }
  }

  return (
    <section
      className={twMerge("px-4 py-16", fullWidth ? "px-0" : "", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
      style={{ backgroundColor: sectionBackgroundColor }}>
      <div className={twMerge("mx-auto", fullWidth ? "max-w-none px-4" : "max-w-4xl")}>
        {section.headline && (
          <h2
            className={twMerge("mb-6 text-3xl font-bold", layout === "centered" ? "text-center" : "text-left")}
            style={{
              fontFamily: theme?.fonts?.heading || "serif",
              fontWeight: theme?.fonts?.headingWeight || "700",
              color: headingColor
            }}>
            {section.headline}
          </h2>
        )}
        {section.subheadline && (
          <p
            className={twMerge("mb-8 text-lg", layout === "centered" ? "text-center" : "text-left")}
            style={{
              fontFamily: theme?.fonts?.paragraph || "sans-serif",
              color: bodyColor
            }}>
            {section.subheadline}
          </p>
        )}

        {renderContent()}
      </div>
    </section>
  )
}

export default GenericSection
